import React from "react";
import KendoUploadField from "./components/fields/KendoUploadField";
import DynamicFieldRenderer from "./components/DynamicFieldRenderer";
import useTechnicalSupportForm from "./hooks/useTechnicalSupportForm";


const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

function sanitizePhoneInput(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 32);
}

function ErrorSummary({ title, items }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="row mb-4">
      <div className="container-fluid formAlert">
        <h4>{title}</h4>
        <ul className="list-danger">
          {items.map((item, index) => (
            <li key={`${item}-${index}`}>*{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function highlightQuery(text, query) {
  const safeText = String(text || "");
  const terms = String(query || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!terms.length) {
    return safeText;
  }

  const escapedTerms = terms.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  const regex = new RegExp(`(${escapedTerms.join("|")})`, "ig");
  return safeText.replace(regex, "<span class='highlight'>$1</span>");
}

export default function TechnicalSupportRequestIsland({ config }) {
  const {
    form,
    filteredItems,
    filesByFieldId,
    showDropdownList,
    setShowDropdownList,
    componentErrors,
    fieldErrors,
    serverSideErrors,
    submitResult,
    loading,
    uploading,
    recaptchaError,
    updateField,
    updateComponentValue,
    selectOrganisation,
    addUploadedFile,
    removeFileByNameGuid,
    submitForm,
  } = useTechnicalSupportForm(config);

  const validationItems = [
    fieldErrors.firstNameQA ? config.labels.firstNameLabel : "",
    fieldErrors.surnameQA ? config.labels.surnameLabel : "",
    fieldErrors.emailQA ? config.labels.emailLabel : "",
    fieldErrors.emailQAConfirm ? config.labels.emailConfirmLabel : "",
    fieldErrors.phoneQA ? config.labels.phoneLabel : "",
    (fieldErrors.organisationQA || fieldErrors.otherOrganisationQA) &&
    !config.isGasConsumerQuery
      ? config.labels.organisationLabel
      : "",
    ...form.componentList
      .filter((item) => componentErrors[item.id])
      .map((item) => item.title),
    fieldErrors.tandcQA ? config.labels.disclaimerTitle : "",
    recaptchaError ? config.labels.captchaMissingLabel : "",
  ].filter(Boolean);
  const showEmailConfirmMismatch =
    Boolean(fieldErrors.emailQAConfirm) &&
    form.emailQAConfirm !== form.emailQA;

  if (submitResult) {
    if (Number(submitResult.ResponseCode ?? submitResult.responseCode) === 200) {
      return (
        <div className="resultContent">
          <div className="w-100 text-center mb-4">
            <h1 className="mb-3 text-bolder">{config.labels.thankYouTitle}</h1>
            <h2 className="text-bolder">{form.firstNameQA}</h2>
          </div>
          <div className="refNumber-cover blueBarTop">
            <div className="p-3">
              <h4 className="section-title">{config.labels.thankYouMessage}</h4>
              <div className="refNumber">
                <strong>{config.labels.referenceTitle}</strong>
                <h2>
                  <strong>{submitResult.ReferenceID ?? submitResult.referenceID}</strong>
                </h2>
              </div>
            </div>
          </div>
          {config.thankYouContentTitleHtml || config.thankYouContentHtml ? (
            <div className="my-5 px-4 pt-3 pb-4 bg-lightest-blue">
              {config.thankYouContentTitleHtml ? (
                <h4
                  className="section-title text-corporateBlue"
                              dangerouslySetInnerHTML={{ __html: decodeHtml(config.thankYouContentTitleHtml) }}
                />
              ) : null}
              {config.thankYouContentHtml ? (
                <div
                  className="info"
                              dangerouslySetInnerHTML={{ __html: decodeHtml(config.thankYouContentHtml) }}
                />
              ) : null}
            </div>
          ) : null}
          {config.technicalThankYouPageUrgentMsgHtml && config.showUrgentMessage ? (
            <div
              className="my-5 px-4 pt-3 pb-5 text-dark-blue"
              dangerouslySetInnerHTML={{
                  __html: decodeHtml(config.technicalThankYouPageUrgentMsgHtml),
              }}
            />
          ) : null}
          {config.awarenessPromoHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: decodeHtml(config.awarenessPromoHtml) }} />
          ) : null}
        </div>
      );
    }

    return (
      <div className="resultContent">
        <div className="w-100 text-center my-4">
          <h3 className="text-danger mb-3">{config.labels.errorFlowTitle}</h3>
          <h4 className="text-blue text-bolder">{config.labels.errorFlowSubtitle}</h4>
        </div>
        <div className="refNumber-cover blueBarTop">
          <div className="text-center">
            <h4 className="text-blue mt-5 mb-3">{config.labels.errorFlowDescription}</h4>
            <button type="button" className="btn btn-arrow-left btn-back" onClick={() => window.location.reload()}>
              <span>Back</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submitForm}>
      <div className="w-100 text-center my-4">
        <h1 className="text-bolder">{config.pageTitle}</h1>
        {config.pageSubtitle ? <h2 className="mb-3 text-bolder">{config.pageSubtitle}</h2> : null}
      </div>

      <ErrorSummary title={config.labels.serverValidationTitle} items={serverSideErrors} />
      <ErrorSummary title={config.labels.validationTitle} items={validationItems} />

      {config.showScreenshotsMessage ? (
        <div className="row">
          <div className="w-100 alert alert-green py-2 px-3">
            <div className="h4 m-0 text-bolder">{config.labels.screenshotRecommendation}</div>
          </div>
        </div>
      ) : null}

      {config.isLsoRequest ? (
        <div className="row">
          <div className="w-100 alert alert-danger py-2 px-3">
            <div className="h4 m-0 text-bolder">{config.labels.lsoWarning}</div>
          </div>
        </div>
      ) : null}

      <div className="row">
        <div className="container-fluid">
          <h4 className="section-title">{config.labels.personalDetailsTitle}</h4>
        </div>
        <div className="col-md-4 mb-md-4">
          <div className={`form-group ${fieldErrors.firstNameQA ? "is-invalid" : ""}`}>
            <label htmlFor="firstNameQA">*{config.labels.firstNameLabel}</label>
            <div className="validation-cover">
              <input
                id="firstNameQA"
                type="text"
                className="form-control"
                value={form.firstNameQA}
                onChange={(event) => updateField("firstNameQA", event.target.value)}
                placeholder={config.labels.firstNamePlaceholder}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-md-4">
          <div className={`form-group ${fieldErrors.surnameQA ? "is-invalid" : ""}`}>
            <label htmlFor="surnameQA">*{config.labels.surnameLabel}</label>
            <div className="validation-cover">
              <input
                id="surnameQA"
                type="text"
                className="form-control"
                value={form.surnameQA}
                onChange={(event) => updateField("surnameQA", event.target.value)}
                placeholder={config.labels.surnamePlaceholder}
              />
            </div>
          </div>
        </div>
        {fieldErrors.emailQA || fieldErrors.firstNameQA || fieldErrors.surnameQA  || fieldErrors.phoneQA ? (
          <div className="col-md-4 mb-md-4 d-none d-md-block">
            <div className="text-danger mt-4 py-2">
              <i
                dangerouslySetInnerHTML={{
                  __html: `*${config.labels.personalDetailsErrorHtml}`,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.emailQA ? "is-invalid" : ""}`}>
            <label htmlFor="emailQA">*{config.labels.emailLabel}</label>
            <div className="validation-cover">
              <input
                id="emailQA"
                type="email"
                className="form-control"
                value={form.emailQA}
                onChange={(event) => updateField("emailQA", event.target.value)}
                placeholder={config.labels.emailPlaceholder}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.emailQAConfirm ? "is-invalid" : ""}`}>
            <label htmlFor="emailQAConfirm">*{config.labels.emailConfirmLabel}</label>
            <div className="validation-cover">
              <input
                id="emailQAConfirm"
                type="email"
                className="form-control"
                value={form.emailQAConfirm}
                onChange={(event) => updateField("emailQAConfirm", event.target.value)}
                placeholder={config.labels.emailConfirmPlaceholder}
              />
            </div>
          </div>
        </div>
        {showEmailConfirmMismatch ? (
          <div className="col-md-4 mb-md-4 d-none d-md-block">
            <div className="text-danger mt-4 py-2">
              <i
                dangerouslySetInnerHTML={{
                  __html: `*${config.labels.personalDetailsEmailConfirmErrorHtml}`,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
      <div className="row mb-4">
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.phoneQA ? "is-invalid" : ""}`}>
            <label htmlFor="phoneQA">*{config.labels.phoneLabel}</label>
            <div className="validation-cover">
              <input
                id="phoneQA"
                type="text"
                className="form-control"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.phoneQA}
                onChange={(event) => updateField("phoneQA", sanitizePhoneInput(event.target.value))}
                placeholder={config.labels.phonePlaceholder}
              />
            </div>
          </div>
        </div>
        {fieldErrors.emailQA || fieldErrors.firstNameQA || fieldErrors.surnameQA ? (
          <div className="col-md-4 d-md-none mb-3">
            <div className="text-danger">
              <i
                dangerouslySetInnerHTML={{
                  __html: `*${config.labels.personalDetailsErrorHtml}`,
                }}
              />
            </div>
          </div>
        ) : null}
        {showEmailConfirmMismatch ? (
          <div className="col-md-4 d-md-none mb-3">
            <div className="text-danger">
              <i
                dangerouslySetInnerHTML={{
                  __html: `*${config.labels.personalDetailsEmailConfirmErrorHtml}`,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="row" aria-live="polite">
        <div className="container-fluid blueBarTop bg-lightest-blue">
          <h4 className="section-title">{config.labels.organisationDetailsTitle}</h4>
          <div className="row">
            <div className="col-md-4 mb-md-4">
              <div className={`form-group filter-dropdown ${fieldErrors.organisationQA ? "is-invalid" : ""}`}>
                <label htmlFor="organisationQA">*{config.labels.organisationLabel}</label>
                <div className="validation-cover">
                  <input
                    id="organisationQA"
                    type="text"
                    className="form-control"
                    autoComplete="off"
                    value={form.organisationQA}
                    placeholder={config.labels.organisationPlaceholder}
                    onFocus={() => setShowDropdownList(true)}
                    onChange={(event) => {
                      updateField("organisationQA", event.target.value);
                      updateField("organisationIdQA", "");
                      updateField("otherOrganisationQA", "");
                      setShowDropdownList(true);
                    }}
                  />
                </div>
                {showDropdownList && filteredItems.length ? (
                  <div className="dropdown d-block p-0">
                    <ul className="dropdown-list">
                      {filteredItems.map((item) => (
                        <li key={`${item.id}-${item.combinedTitle}`}>
                          <button type="button" className="dropdown-item" onClick={() => selectOrganisation(item)}>
                            {Number(item.id) === -1 && config.allowNotListedOrganisation ? (
                              <b dangerouslySetInnerHTML={{ __html: highlightQuery(item.combinedTitle, form.organisationQA) }} />
                            ) : (
                              <span dangerouslySetInnerHTML={{ __html: highlightQuery(item.combinedTitle, form.organisationQA) }} />
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
            {Number(form.organisationIdQA) === -1 && config.allowNotListedOrganisation ? (
              <div className="col-md-4 mb-md-4">
                <div className={`form-group ${fieldErrors.otherOrganisationQA ? "is-invalid" : ""}`}>
                  <label htmlFor="otherOrganisationQA">*{config.labels.otherOrganisationLabel}</label>
                  <div className="validation-cover">
                    <input
                      id="otherOrganisationQA"
                      type="text"
                      className="form-control"
                      value={form.otherOrganisationQA}
                      placeholder={config.labels.otherOrganisationPlaceholder}
                      onFocus={() => setShowDropdownList(false)}
                      onChange={(event) => updateField("otherOrganisationQA", event.target.value)}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {config.showIssue || form.componentList.length ? (
        <div className="row mb-4">
          <div className="container-fluid blueBarTop">
            <h4 className="section-title">{config.labels.issueSectionTitle}</h4>
            {config.introHtml ? (
              <div className="mb-4" dangerouslySetInnerHTML={{ __html: config.introHtml }} />
            ) : null}
            <div className="row">
              <div className="col-md-8">
                <div className="row">
                  {form.componentList.map((field) => (
                    <DynamicFieldRenderer
                      key={field.id}
                      field={field}
                      componentList={form.componentList}
                      componentErrors={componentErrors}
                      onFieldChange={updateComponentValue}
                      labels={config.labels}
                      filesByFieldId={filesByFieldId}
                      allowedExtensions={config.allowedExtensions}
                      fileSizeLimit={config.fileSizeLimit}
                      limitOfFileNumber={config.limitOfFileNumber}
                      onUploadSuccess={addUploadedFile}
                      onRemoveSuccess={removeFileByNameGuid}
                      uploading={uploading}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {config.showDescriptionAttachmentFields ? (
        <div className="row">
          <div className="container-fluid blueBarTop bg-lightest-blue pb-5">
            <h4 className="section-title">{config.labels.documentationTitle}</h4>
            <p className="text-darkBlue mb-2">
              <i>
                <strong>{config.labels.documentationSubtitleStrong}</strong>
              </i>
            </p>
            <p className="text-darkBlue">
              <i>{config.labels.documentationFileTypes}</i>
            </p>
            <div className="row">
              <div className="col-md-8">
                <KendoUploadField
                  fieldId="filesQA"
                  title={config.labels.documentationLabel}
                  disabled={uploading}
                  multiple
                  currentFiles={filesByFieldId.filesQA || []}
                  allowedExtensions={config.allowedExtensions}
                  fileSizeLimit={config.fileSizeLimit}
                  limitOfFileNumber={config.limitOfFileNumber}
                  onUploadSuccess={(uploadedFile) =>
                    addUploadedFile(
                      { id: "filesQA", title: config.labels.documentationLabel },
                      uploadedFile,
                      true,
                    )
                  }
                  onRemoveSuccess={removeFileByNameGuid}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="row">
        <div className={`container-fluid ${config.showDescriptionAttachmentFields ? "blueBarTop" : ""}`}>
          <h4 className="section-title">{config.labels.disclaimerTitle}</h4>
          <div className="row">
            <div className="col-md-5">
              <div className={`form-group tandcCheckbox ${fieldErrors.tandcQA ? "is-invalid" : ""}`}>
                <div className="validation-cover">
                  <input
                    id="tandcQA"
                    type="checkbox"
                    checked={form.tandcQA}
                    onChange={(event) => updateField("tandcQA", event.target.checked)}
                  />
                  <label htmlFor="tandcQA">{config.labels.termsCheckboxLabel}</label>
                </div>
              </div>
            </div>
            {fieldErrors.tandcQA ? (
              <div className="col-md-4 d-flex align-items-center">
                <div className="text-danger">
                  <i
                    dangerouslySetInnerHTML={{
                      __html: `*${config.labels.disclaimerErrorHtml}`,
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="row">
            <div className="col-md-5">
              <div className="tandcDesc" dangerouslySetInnerHTML={{ __html: config.labels.termsDescriptionHtml }} />
            </div>
          </div>
          <div className={`form-group mt-5 ${recaptchaError ? "is-invalid" : ""}`}>
            <div className="validation-cover">
              <div id="react-island-recaptcha" />
            </div>
          </div>
        </div>
      </div>

      <div className="row my-4">
        <div className="col-6 col-md-3 offset-md-3 column mb-3 pl-0">
          <a href={config.cancelUrl} className="btn btn-wide btn-arrow-left btn-back">
            <span>{config.labels.cancelLabel}</span>
          </a>
        </div>
        <div className="col-6 col-md-3 column mb-3 pr-0">
          <button type="submit" className="btn btn-wide btn-arrow-right btn-next" disabled={loading || uploading}>
            <span>{loading ? config.labels.submittingLabel : config.labels.submitLabel}</span>
          </button>
        </div>
      </div>
    </form>
  );
}
